import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Zap, Plus, Check, Users, Target, Calendar } from "lucide-react";
import Layout from "@/components/Layout";
import api from "@/lib/api";

const NICHES = ["affiliate", "dropshipping", "freelance", "crypto", "content", "health", "ai"];

export default function Challenges() {
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", target_amount: 1000, days: 30, niche: "affiliate" });

  const load = async () => {
    const { data } = await api.get("/challenges");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.title.trim()) return;
    await api.post("/challenges", form);
    setForm({ title: "", description: "", target_amount: 1000, days: 30, niche: "affiliate" });
    setCreating(false);
    load();
  };

  const toggleJoin = async (id) => {
    await api.post(`/challenges/${id}/join`);
    load();
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto py-8 px-4 md:px-8">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-3">
              <Zap size={22} className="text-[#00E676]" strokeWidth={2} />
              <h1 className="font-heading font-bold text-4xl tracking-tight">Challenges</h1>
            </div>
            <p className="text-sm text-white/45 font-mono mt-1">Public goals. Daily progress. Real accountability.</p>
          </div>
          <button
            data-testid="new-challenge-btn"
            onClick={() => setCreating(!creating)}
            className="cta-green rounded-md px-4 py-2 text-sm font-semibold flex items-center gap-1.5"
          >
            <Plus size={14} strokeWidth={2.5} /> {creating ? "Cancel" : "Launch challenge"}
          </button>
        </div>

        {creating && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="surface rounded-xl p-5 mb-6 space-y-3">
            <input
              data-testid="new-ch-title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder='e.g. "$0 → $1,000 in 30 days"'
              className="w-full bg-white/[0.04] border border-white/8 rounded-md px-3 py-2 focus:outline-none focus:border-[#00E676]/40"
            />
            <textarea
              data-testid="new-ch-desc"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="The rules. The plan. What counts as winning."
              rows={3}
              className="w-full bg-white/[0.04] border border-white/8 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#00E676]/40"
            />
            <div className="grid grid-cols-3 gap-2">
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#00E676] font-mono">$</span>
                <input
                  data-testid="new-ch-target"
                  type="number"
                  value={form.target_amount}
                  onChange={(e) => setForm({ ...form, target_amount: Number(e.target.value) })}
                  className="w-full bg-white/[0.04] border border-white/8 rounded-md pl-7 pr-3 py-2 text-sm font-mono focus:outline-none focus:border-[#00E676]/40"
                />
              </div>
              <input
                data-testid="new-ch-days"
                type="number"
                value={form.days}
                onChange={(e) => setForm({ ...form, days: Number(e.target.value) })}
                placeholder="days"
                className="bg-white/[0.04] border border-white/8 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#00E676]/40"
              />
              <select
                data-testid="new-ch-niche"
                value={form.niche}
                onChange={(e) => setForm({ ...form, niche: e.target.value })}
                className="bg-white/[0.04] border border-white/8 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#00E676]/40"
              >
                {NICHES.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <button data-testid="new-ch-create" onClick={create} className="cta-green rounded-md px-5 py-2 text-sm font-bold">
              Launch challenge
            </button>
          </motion.div>
        )}

        {items.length === 0 ? (
          <div className="surface rounded-xl p-12 text-center">
            <Zap size={32} className="text-[#00E676] mx-auto mb-3" strokeWidth={1.5} />
            <div className="font-heading font-bold text-xl mb-1">No challenges yet.</div>
            <div className="text-sm text-white/50">Launch the first $0 → $1k challenge.</div>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {items.map((c, i) => (
              <motion.div
                key={c.challenge_id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className="surface rounded-xl p-6 card-hover relative overflow-hidden"
                data-testid={`challenge-${c.challenge_id}`}
              >
                <div className="absolute top-0 right-0 w-32 h-32 bg-[#00E676]/5 rounded-full blur-3xl pointer-events-none" />
                <div className="relative">
                  <div className="flex items-center gap-2 mb-3 text-xs font-mono uppercase tracking-wider text-white/45">
                    {c.niche && <span className="verified-pill px-2 py-0.5 rounded">{c.niche}</span>}
                    <span className="flex items-center gap-1"><Calendar size={11} /> {c.days} days</span>
                  </div>
                  <h3 className="font-heading font-bold text-2xl tracking-tight mb-2">{c.title}</h3>
                  <p className="text-sm text-white/60 leading-relaxed mb-4 line-clamp-3">{c.description}</p>

                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="surface-elev rounded-md p-3">
                      <div className="text-[10px] font-mono uppercase tracking-wider text-white/40">Target</div>
                      <div className="font-mono text-xl text-[#00E676] tracking-tight font-semibold">
                        ${c.target_amount.toLocaleString()}
                      </div>
                    </div>
                    <div className="surface-elev rounded-md p-3">
                      <div className="text-[10px] font-mono uppercase tracking-wider text-white/40">Joined</div>
                      <div className="font-mono text-xl tracking-tight font-semibold flex items-center gap-1.5">
                        <Users size={14} className="text-white/60" /> {c.participants_count}
                      </div>
                    </div>
                  </div>

                  <button
                    data-testid={`join-challenge-${c.challenge_id}`}
                    onClick={() => toggleJoin(c.challenge_id)}
                    className={`w-full rounded-md py-2.5 font-semibold text-sm flex items-center justify-center gap-1.5 transition ${
                      c.joined ? "surface-elev text-[#00E676]" : "cta-green"
                    }`}
                  >
                    {c.joined ? <><Check size={14} strokeWidth={2.5} /> Joined</> : <><Target size={14} strokeWidth={2.5} /> Join challenge</>}
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
